import {EventEmitter, useAsync2} from "nate-react-api-helpers";
import {api} from "../../../api/API";
import {useCallback, useContext, useEffect} from "react";
import {
    DriverRoute,
    PurchaseOrder, PurchaseOrderItem, RouteStop
} from "../../../api/Logistics";
import {Loader} from "../../../misc/Loader";
import Business from "@mui/icons-material/Business";
import {PickupPoItemRow} from "./PickupPoItemRow";
import LocalShipping from "@mui/icons-material/LocalShipping";
import {RouteItemRow} from "./RouteItemRow";
import {DropPoItemRow} from "./DropPoItemRow";
import {DropDeliveryRow} from "./DropDeliveryRow";
import {routePopupPanel} from "./RouteList";
import {useSyncedRef} from "../../../misc/SyncedRef";
import {grey} from "@mui/material/colors";
import {DialogContent, DialogTitle, Grid} from "@mui/material";
import {Dialog2} from "../../../misc/Dialog2";
import {MenuButton} from "../purchasing/MenuButton";
import {projectLabel} from "../../../api/Projects";
import {usePrintParallel} from "../../project/shopdrawing/changeset/PrintParallel";
import {PackingSlipPDF} from "../deliveries/PackingSlipPDF";
import {Delivery, DeliveryItem} from "../../../api/Deliveries";
import {CustomTextGroupRow} from "./CustomTextGroupRow";
import {WebSocketContext} from "../../../misc/WebSocketProvider";
import {useSnackbar} from "../../../misc/Snackbar";
import {getAll} from "./useGetAll";
import {Link} from "react-router-dom";
import {tykelWarehouseLocation} from "../../../misc/pdf/parts/CoverPage";

export const RefreshRouteItems = new EventEmitter<{
    updatedByUs: boolean;
}>();

export const RouteIsRealtime = new EventEmitter<boolean>();

export function useIsPurchaseOrderItemGoingToWarehouse(data: RouteStop[]) {
    return useCallback((purchaseOrderItem: number) => {
        for(let i = 0; i < data.length; i++) {
            const stop = data[i];
            if(!stop.groups) continue
            if(!stop.tykelWarehouse) continue

            for(let j = 0; j < stop.groups.length; j++) {
                const group = stop.groups[j]
                if(!group.dropoffPurchaseOrder) continue;

                const item = (group.items || [])?.find(g => g.purchaseOrderItem === purchaseOrderItem)
                if(item) return true;
            }
        }

        return false;
    }, [data]);
}

export function useIsPurchaseOrderDelivered(data: RouteStop[]) {
    return useCallback((purchaseOrder: number) => {
        for(let i = 0; i < data.length; i++) {
            const stop = data[i];
            if(!stop.groups) continue

            for(let j = 0; j < stop.groups.length; j++) {
                const group = stop.groups[j]

                // there may be multiple groups for the same purchase order
                if(group.dropoffPurchaseOrder === purchaseOrder) {
                    if(group.completed) return true;
                }
            }
        }

        return false;
    }, [data]);
}

export function RouteItems(props: {
    route: number;
    routeObj?: DriverRoute;
}) {
    const route = props.route;

    const list = useAsync2((props: { route: number}) => {
        return api.logistics.listRouteStops(props);
    }, {route: route}, [route]);

    const reloadList = useSyncedRef(list.reload)
    const wsContext = useContext(WebSocketContext);
    const snack = useSnackbar();
    const snackRef = useSyncedRef(snack);

    useEffect(() => {
        let showingSnack = false;
        const cancel = wsContext.handle("route:updated", msg => {
            if(msg.route === props.route && !showingSnack) {
                showingSnack = true
                snackRef.current.showAction("Route updated by driver. Reload to see changes.", [{
                    title: "Reload",
                    callback: () => {
                        reloadList.current();
                        snackRef.current.hide();
                        showingSnack = false;
                    }
                }])
            }
        })

        const hide = snackRef.current.hide;

        return () => {
            cancel();

            if(showingSnack) {
                hide();
            }
        }
    }, [wsContext, props.route, reloadList, snackRef]);

    useEffect(() => {
        const sub = routePopupPanel.fromClient.subscribeAndFireLast(v => {
            if (v === "refresh-route") {
                reloadList.current();
            }
        })

        return () => sub.cancel();
    },[reloadList]);

    useEffect(() => {
        const sub = RefreshRouteItems.subscribe(() => {
            reloadList.current();
        });

        return () => sub.cancel()
    }, [reloadList])

    const ctx = usePrintParallel();

    const data = list.asList;
    const packingSlipGroups = data.filter(v => !!v.project && v.groups && v.groups.length > 0)

    const hasStarted = !!data.find(d => d.driverViewed || (d.groups && !!d.groups.find(g => g.completed)))
    useEffect(() => {
        RouteIsRealtime.emit(hasStarted);
    }, [hasStarted]);

    const isPurchaseOrderItemGoingToWarehouse = useIsPurchaseOrderItemGoingToWarehouse(data)
    const isPurchaseOrderDelivered = useIsPurchaseOrderDelivered(data)

    return (
        <div style={{flex: 1, display: "flex", flexDirection: "column", overflow: "auto"}}>
            <Loader {...list} />
            {data.map((d, index) => {
                const prevGroup = index > 0 ? data[index-1] : null;
                const nextGroup = index === data.length -1 ? null : data[index+1];

                return (
                    <RouteItemRow value={d} icon={rowIcon(d)}
                      title={rowTitle(d)}
                      driverAt={d.driverAt}
                      address={makeAddress(d)}
                      next={nextGroup}
                      prev={prevGroup}
                      disabled={props.routeObj?.completed}
                    >
                        <table>
                            {d.groups?.map(c => {
                                if(c.pickupPurchaseOrder) return <PickupPoItemRow
                                    value={c} routeCompleted={props.routeObj?.completed || false}
                                    isPurchaseOrderItemGoingToWarehouse={isPurchaseOrderItemGoingToWarehouse}
                                    isPurchaseOrderDelivered={isPurchaseOrderDelivered}
                                />
                                if(c.dropoffPurchaseOrder) return <DropPoItemRow value={c} routeCompleted={props.routeObj?.completed || false} />
                                if(c.delivery) return <DropDeliveryRow
                                    value={c}
                                    routeCompleted={props.routeObj?.completed || false}
                                    canRemove={!d.tykelWarehouse} /* remove from drop side */
                                />

                                if(c.customTextGroup) {
                                    return (
                                        <CustomTextGroupRow
                                            value={c}
                                            routeCompleted={props.routeObj?.completed || false}
                                        />
                                    )
                                }

                                return null;
                            })}
                        </table>
                    </RouteItemRow>
                )
            })}
            <div style={{flex: 1}} />

            {ctx.loading && <Dialog2 open>
                <DialogTitle>Printing...</DialogTitle>
                <DialogContent>
                    <Loader loading={true} error={null} />
                </DialogContent>
            </Dialog2>}

            <div style={{padding: 8, paddingRight: 16, borderTop: "1px solid " + grey["300"],backgroundColor: grey["200"]}}>
                <Grid container justifyContent="flex-end">
                    <Grid item>
                        <MenuButton
                            options={[{
                                name: "Export Packing Slip",
                                disabled: packingSlipGroups.length === 0,
                                onClick: async () => {
                                    packingSlipGroups
                                        .map(gr => {
                                            if(!gr.project) return;

                                            const projectId = gr.project;
                                            const projectP = api.projects.get(projectId);

                                            gr.groups?.map(async (ch) => {

                                                let value: Delivery | PurchaseOrder;
                                                let items: (DeliveryItem|PurchaseOrderItem)[]

                                                const dropDel = ch.delivery;
                                                const dropPO = ch.dropoffPurchaseOrder;

                                                if(dropDel) {
                                                    const delP = api.deliveries.getDelivery({id: dropDel});
                                                    const delItemsP = getAll(null, offset => api.deliveries.listDeliveryItems({delivery: dropDel, offset}));
                                                    value = await delP;
                                                    items = await delItemsP;

                                                    items = items.filter(v => {
                                                        if(!ch.items) return false;
                                                        const routeItem = ch.items.find(i => i.deliveryItem === v.id)
                                                        if(!routeItem) return false;
                                                        v.qty = routeItem.qty;
                                                        return true;
                                                    })
                                                } else if(dropPO) {
                                                    const poP = api.logistics.getPO({id: dropPO});
                                                    const poItemsP =  getAll(null, offset => api.logistics.listPurchaseOrderItems({purchaseOrder: dropPO, offset}));

                                                    value = await poP;
                                                    items = await poItemsP;

                                                    items = items.filter(v => {
                                                        if(!ch.items) return false;
                                                        const routeItem = ch.items.find(i => i.purchaseOrderItem === v.id)
                                                        if(!routeItem) return false;
                                                        v.qty = routeItem.qty;
                                                        return true;
                                                    })
                                                } else {
                                                    return null;
                                                }

                                                const project = await projectP;

                                                ctx.printer({
                                                    name: `packing-slip-${dropDel||"PO" + dropPO}.pdf`,
                                                    render: async onReady => <PackingSlipPDF project={project} items={items} value={value} onReady={onReady} />,
                                                    download: true,
                                                    serverInfo: {
                                                        submissionForProject: projectId,
                                                        submissionType: "route-packing-slip",
                                                        ignoreSubmissionDate: true,
                                                    }
                                                })
                                            })
                                        })

                                }
                            }]}
                        >
                            Export
                        </MenuButton>
                    </Grid>
                </Grid>
            </div>
        </div>
    )
}

export function rowIcon(d: RouteStop) {
    return d.manufacturer ? <Business fontSize="inherit" /> :
        d.project ? <LocalShipping fontSize="inherit" /> :
            <LocalShipping fontSize="inherit" />
}

export function rowTitle(d: RouteStop, link: boolean = false) {
    if(d.manufacturer) {
        if(link) {
            return <>Pickup at <Link to={"/settings/supplier/" + d.manufacturer}>{d.manufacturerName}</Link></>
        }

        return `Pickup at ${d.manufacturerName}`
    }

    if(d.project) {
        if(link) {
            return <>Stop at <Link to={"/project/" + d.project}>{projectLabel({projectNumber: d.projectNumber, name: d.projectName})}</Link></>
        }

        return `Stop at ${projectLabel({projectNumber: d.projectNumber, name: d.projectName})}`
    }

    if(d.tykelWarehouse) {
        return `Tykel Warehouse`
    }

    if(d.customTextStop) {
        return d.customTextStop
    }

    return "Unknown"
}

export function sanitizeFileName(input: string): string {
    return input.replace(/[^A-z0-9- ._]+/g, "-").replace(/[-]{2,}/g, "-")
}

export type Address = {
    street: string;
    city: string;
    postal: string;
}

export function makeAddress(input: RouteStop): Address | null {
    const addr = {
        street: input.addressStreet || "",
        city: input.addressCity || "",
        postal: input.addressPostal || ""
    }

    if(addr.street === "" && addr.city === "" && addr.postal === "") {
        if(input.tykelWarehouse) {
            return tykelWarehouseLocation
        }

        return null;
    }

    return addr;
}